/**
 * Authentication Interceptor Service
 * Handles automatic token refresh and authentication errors
 */

class AuthInterceptor {
  constructor() {
    this.isRefreshing = false;
    this.failedQueue = [];
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second
  }

  /**
   * Process failed queue after token refresh
   */
  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });
    
    this.failedQueue = [];
  }

  /**
   * Try to refresh the token
   */
  async refreshToken() {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('token', data.token);
        return data.token;
      } else {
        throw new Error('Token refresh failed');
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[AuthInterceptor] Token refresh failed:', error);
      }
      throw error;
    }
  }

  /**
   * Enhanced fetch with automatic retry and token refresh
   */
  async authenticatedFetch(url, options = {}, retryCount = 0) {
    const token = localStorage.getItem('token');
    
    // Add authorization header if not present
    if (token && !options.headers?.Authorization) {
      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${token}`
      };
    }

    try {
      const response = await fetch(url, options);
      
      // If unauthorized, try to refresh token
      if (response.status === 401 && retryCount < this.maxRetries) {
        const errorData = await response.json().catch(() => ({}));
        
        // Check if it's a token expiration error
        if (errorData.message === 'Token expired' || errorData.message === 'Invalid token or session expired') {
          
          // If already refreshing, queue this request
          if (this.isRefreshing) {
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            }).then(token => {
              options.headers.Authorization = `Bearer ${token}`;
              return this.authenticatedFetch(url, options, retryCount + 1);
            });
          }

          // Try to refresh token
          this.isRefreshing = true;
          
          try {
            const newToken = await this.refreshToken();
            this.processQueue(null, newToken);
            
            // Retry original request with new token
            options.headers.Authorization = `Bearer ${newToken}`;
            return this.authenticatedFetch(url, options, retryCount + 1);
            
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            
            // If refresh fails, trigger re-authentication
            this.triggerReAuthentication();
            throw new Error('Authentication required');
          } finally {
            this.isRefreshing = false;
          }
        }
      }
      
      return response;
      
    } catch (error) {
      // If it's a network error and we haven't retried too much, try again
      if (retryCount < this.maxRetries && !error.message.includes('Authentication required')) {
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.authenticatedFetch(url, options, retryCount + 1);
      }
      throw error;
    }
  }

  /**
   * Trigger re-authentication modal
   */
  triggerReAuthentication() {
    // Create custom event that the app can listen to
    const event = new CustomEvent('authRequired', {
      detail: {
        message: 'Your session has expired. Please log in again to continue.',
        preserveWork: true
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Save current work state to localStorage before re-auth
   */
  async saveWorkState() {
    try {
      // Get current state from various contexts
      const currentState = {
        timestamp: Date.now(),
        url: window.location.href,
        // This will be filled by the app when triggered
        gameState: null
      };
      
      localStorage.setItem('unsavedWork', JSON.stringify(currentState));
      return true;
    } catch (error) {
      console.error('[AuthInterceptor] Failed to save work state:', error);
      return false;
    }
  }

  /**
   * Restore work state after re-authentication
   */
  getUnsavedWork() {
    try {
      const saved = localStorage.getItem('unsavedWork');
      if (saved) {
        const state = JSON.parse(saved);
        // Only return if it's recent (within last hour)
        if (Date.now() - state.timestamp < 60 * 60 * 1000) {
          return state;
        }
      }
      return null;
    } catch (error) {
      console.error('[AuthInterceptor] Failed to get unsaved work:', error);
      return null;
    }
  }

  /**
   * Clear saved work after successful restore
   */
  clearUnsavedWork() {
    localStorage.removeItem('unsavedWork');
  }
}

// Create singleton instance
const authInterceptor = new AuthInterceptor();

export default authInterceptor; 